import React, { useEffect, useState, useRef } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, TextField, IconButton, InputAdornment } from '@mui/material';
import { useGetCurrencies } from 'api/invoices';
import { CurrencyQueryParams } from 'api/invoices/invoices.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { CurrencyCode } from 'pages/invoices/invoices.types';

interface EditableTruckerRateInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  rowData?: any;
  hasValidationError?: boolean;
}

interface TruckerRateValue {
  rate: number | string;
  currency: CurrencyCode | null;
}

const parseInitialValue = (value: any, rowData?: any): TruckerRateValue => {
  if (typeof value === 'object' && value !== null) {
    const rate = value.rate ?? value.amount ?? '';
    let currency = null;

    if (value.currency) {
      if (typeof value.currency === 'object') {
        currency = value.currency;
      } else if (typeof value.currency === 'string') {
        const currencyId = rowData?.truckerRateCurrencyId;
        currency = {
          currency: value.currency,
          id: currencyId || null,
        } as CurrencyCode;
      }
    }

    return { rate, currency };
  }

  if (typeof value === 'string') {
    const parts = value.trim().split(' ');
    if (parts.length >= 2) {
      const rate = parseFloat(parts[0]) || '';
      const currencyCode = parts.slice(1).join(' ');

      const currencyId = rowData?.truckerRateCurrencyId;

      return {
        rate,
        currency: currencyCode
          ? ({
              currency: currencyCode,
              id: currencyId || null,
            } as CurrencyCode)
          : null,
      };
    } else if (parts.length === 1 && parts[0] !== '') {
      return {
        rate: parseFloat(parts[0]) || parts[0],
        currency: null,
      };
    }
  }
  return { rate: '', currency: null };
};

export const EditableTruckerRateInput: React.FC<
  EditableTruckerRateInputProps
> = ({
  value,
  onChange,
  onSave,
  onCancel,
  rowData,
  hasValidationError = false,
}) => {
  const { currenciesData, loading, getCurrencies } = useGetCurrencies();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMatchedCurrencyRef = useRef<boolean>(false);
  const hasSetDefaultPLNRef = useRef<boolean>(false);
  const [options, setOptions] = useState<CurrencyCode[]>([]);
  const [currencyParams, setCurrencyParams] = useState<CurrencyQueryParams>({
    filterText: '',
    resultSize: 25,
    pageNumber: 1,
    onlyVisible: true,
  });

  const [localValue, setLocalValue] = useState<TruckerRateValue>(() =>
    parseInitialValue(value, rowData),
  );

  useEffect(() => {
    getCurrencies(currencyParams);
  }, [getCurrencies, currencyParams]);

  useEffect(() => {
    if (currenciesData?.length) {
      const filteredOptions = currenciesData.filter(
        (currency) => currency.enabled,
      );

      setOptions((prev) => {
        const map = new Map(prev.map((currency) => [currency.id, currency]));

        filteredOptions.forEach((currency) => {
          map.set(currency.id, currency);
        });

        return Array.from(map.values());
      });

      if (
        !hasMatchedCurrencyRef.current &&
        localValue.currency?.currency &&
        !localValue.currency.id
      ) {
        const matchingCurrency = filteredOptions.find(
          (currency) => currency.currency === localValue.currency?.currency,
        );
        if (matchingCurrency) {
          hasMatchedCurrencyRef.current = true;
          setLocalValue((prev) => ({
            ...prev,
            currency: matchingCurrency,
          }));
        }
      }

      if (
        !hasSetDefaultPLNRef.current &&
        !localValue.rate &&
        !localValue.currency
      ) {
        const plnCurrency = filteredOptions.find(
          (currency) => currency.currency === 'PLN',
        );
        if (plnCurrency) {
          hasSetDefaultPLNRef.current = true;
          const newValue = { ...localValue, currency: plnCurrency };
          setLocalValue(newValue);
          onChange(formatValueForSave(newValue));
        }
      }
    }
  }, [
    currenciesData,
    localValue.currency?.currency,
    localValue.currency?.id,
    localValue.rate,
    onChange,
  ]);

  const formatValueForSave = (rateValue: TruckerRateValue): any => ({
    rate: rateValue.rate || null,
    currency: rateValue.currency || null,
  });

  const handleRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRate = event.target.value;
    const newValue = { ...localValue, rate: newRate };
    setLocalValue(newValue);
    onChange(formatValueForSave(newValue));
  };

  const handleCurrencyChange = (_: any, newCurrency: CurrencyCode | null) => {
    const newValue = { ...localValue, currency: newCurrency };
    setLocalValue(newValue);
    onChange(formatValueForSave(newValue));
  };

  const handleCurrencySearchChange = (newValue: string) => {
    setOptions([]);
    setCurrencyParams((prev) => ({
      ...prev,
      filterText: newValue,
      pageNumber: 1,
    }));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Enter') {
      onSave(formatValueForSave(localValue));
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const currentFocus = document.activeElement;

      if (
        !containerRef.current ||
        !containerRef.current.contains(currentFocus)
      ) {
        onSave(formatValueForSave(localValue));
      }
    }, 200);
  };

  const handleRateBlur = () => {
    handleBlur();
  };

  const handleClearRate = () => {
    const clearedValue = { ...localValue, rate: '' };
    setLocalValue(clearedValue);
    onChange(formatValueForSave(clearedValue));
  };

  return (
    <Box
      ref={containerRef}
      display='flex'
      gap={0.5}
      alignItems='center'
      onKeyDown={handleKeyDown}
      sx={{ width: '220px', height: 'auto' }}
      data-trucker-rate-container
    >
      <TextField
        size='small'
        type='number'
        value={localValue.rate}
        onChange={handleRateChange}
        onBlur={handleRateBlur}
        InputProps={{
          endAdornment: localValue.rate ? (
            <InputAdornment position='end'>
              <IconButton
                size='small'
                onClick={handleClearRate}
                onMouseDown={(e) => e.preventDefault()}
                sx={{ padding: '1px', marginRight: '-4px' }}
              >
                <ClearIcon sx={{ fontSize: '12px' }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          width: '110px',
          minWidth: '110px',
          margin: 0,
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            fontSize: '0.7rem',
          },
          '& .MuiInputBase-input': {
            padding: '0px 8px',
            lineHeight: '1.5em',
            height: '24px',
            fontSize: '0.7rem',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '&[type=number]': {
              MozAppearance: 'textfield',
            },
          },
        }}
        inputProps={{
          step: '1',
          min: '0',
        }}
      />

      <Box
        sx={{
          width: '110px',
          minWidth: '110px',
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            padding: '0px 8px',
            fontSize: '0.7rem',
          },
          '& .MuiInputBase-input': {
            padding: '0px',
            height: '24px',
            lineHeight: '1.5em',
            fontSize: '0.7rem',
          },
          '& .MuiAutocomplete-endAdornment': {
            top: 'calc(50% - 10px)',
            ...(hasValidationError && { right: '26px !important' }),
          },
          '& .MuiAutocomplete-clearIndicator': {
            visibility: 'visible',
            padding: '1px',
            '& .MuiSvgIcon-root': { fontSize: '12px' },
          },
          '& .MuiAutocomplete-popupIndicator': {
            padding: '1px',
            '& .MuiSvgIcon-root': { fontSize: '12px' },
          },
          '& .MuiCircularProgress-root': {
            width: '12px !important',
            height: '12px !important',
          },
        }}
      >
        <AutocompletePaginationAsync
          padding='dense'
          width={110}
          value={localValue.currency}
          loading={loading}
          options={options}
          isOptionEqualToValue={(option, selectedValue) => {
            if (!selectedValue) return false;
            if (
              typeof selectedValue === 'object' &&
              'currency' in selectedValue
            ) {
              return (
                option?.currency === selectedValue.currency ||
                option?.id === selectedValue.id
              );
            }
            return false;
          }}
          getOptionLabel={(option) => {
            if (!option) return '';
            return option.currency || '';
          }}
          handleSearchChange={handleCurrencySearchChange}
          onChange={handleCurrencyChange}
          onScrollBottom={() =>
            setCurrencyParams((prev) => ({
              ...prev,
              pageNumber: (prev.pageNumber || 0) + 1,
            }))
          }
          onBlur={handleBlur}
          {...({
            componentsProps: {
              paper: {
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.7rem',
                    padding: '2px 8px',
                    minHeight: '28px',
                  },
                  '& .MuiAutocomplete-noOptions': {
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                  },
                },
              },
            },
          } as any)}
        />
      </Box>
    </Box>
  );
};
